// No module can be bought while the group's own card subscription is paused
// (Dave, 2026-10-05). A module change needs a live subscription to add the
// line to, and a paused one is not live: the api answers a module change with
// 409 subscription_paused, and billing offers only the portal. So every place
// that sends an owner to billing to buy a module, while paused, offers no
// purchase and names no price. It says the subscription is paused, that the
// modules on it come back when it resumes and others can be added then, and
// gives the owner Manage billing, where billing is managed, wherever it would
// have given them a billing button.
// It goes where the paused banner's Manage billing goes (useGroupBillingTarget,
// one rule for both): Manage subscription, deep-linked to the group and to
// resuming it, once the billing-accounts list says the owner manages the
// group; Subscription & billing before that (an api that does not serve the
// list yet). Not "where it resumes": in 1a Manage subscription says resuming
// is support's, and whether Stripe's portal can resume a paused subscription
// is unverified. Nothing here says how it resumes.
//
// A PAUSED GROUP HOLDS NONE OF ITS CARD MODULES. The api writes every row on
// the paused subscription, its modules' included, as paused, and a paused row
// is not live: the business's `modules` and billing's `entitled` say false for
// the very modules the group gets back when it resumes. Only a module held off
// the card (a comp, a rule's grant) is still held. Nothing here can tell a
// module on the paused subscription from one never bought, so nothing says
// "not on the subscription" or "Not added" while paused, and the line says
// what holds for both.
//
// IN THE PAUSED BANNER'S VOICE: "Your Business subscription" to the owner,
// "The owner's Business subscription" to anyone else told of the pause.
//
// WHERE "PAUSED" COMES FROM, never a fetch of its own: the business payload's
// billing alert, which the api sends `paused` only to the owner and the
// accountant and only while the pause leaves the books read-only; or, on a
// screen that already reads GET /business/billing (the Marketplace), its
// `paused`. A viewer told nothing keeps today's offer.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XStack } from 'tamagui';
import { GBButton, GBText } from '@greenbackapp/ui';
import { useBusinessOptional } from '../../../contexts/BusinessContext';
import { useManagedGroupBilling } from '../../../hooks/useBillingAccounts';
import { manageSubscriptionPath } from '../../../pages/manageSubscriptionModel';
import { BillingAttentionTask } from '../../../services/billingAccountsService';
import { BusinessBillingAlert } from '../../../types/businessApi';
import type { ModuleLock } from '../../prospects/prospectScope';

export const BUSINESS_BILLING_PATH = '/business/settings/billing';
export const MANAGE_BILLING_LABEL = 'Manage billing';

/** What follows the pause, whoever is told: true of a module on it, and of one never bought. */
const PAUSED_MODULES_RESUME = 'Modules on it come back when it resumes; others can be added then.';

/** Said to the owner in place of a module offer while the subscription is paused. */
export const PAUSED_MODULES_MESSAGE_OWNER = `Your Business subscription is paused. ${PAUSED_MODULES_RESUME}`;

/** Said to anyone else told of the pause (the accountant), in place of a module offer. */
export const PAUSED_MODULES_MESSAGE_NOT_OWNER = `The owner's Business subscription is paused. ${PAUSED_MODULES_RESUME}`;

export interface PausedModuleOffer {
  /** The group's own card subscription is paused, as far as this viewer has been told. */
  paused: boolean;
  /** Offer Manage billing: only the owner manages the subscription, and only while it is paused. */
  canManageBilling: boolean;
  /** What is said in place of a module offer while paused, in the paused banner's voice for this role. */
  message: string;
  /**
   * Where Manage billing goes (useGroupBillingTarget). Required, so every
   * offer built by hand says where it sends the owner.
   */
  billingPath: string;
}

export const pausedModuleOffer = (
  isOwner: boolean,
  paused: boolean,
  billingPath: string = BUSINESS_BILLING_PATH
): PausedModuleOffer => ({
  paused,
  canManageBilling: paused && isOwner,
  message: isOwner ? PAUSED_MODULES_MESSAGE_OWNER : PAUSED_MODULES_MESSAGE_NOT_OWNER,
  billingPath,
});

/**
 * Where an owner manages this group's bill, for a task or none: Manage
 * subscription, deep-linked to the group (PRD 114 §4.5), once the list says
 * they manage it and that page can open its billing (useManagedGroupBilling);
 * otherwise (an api that does not serve the list, one still on its way, or a
 * group with no portal there) Subscription & billing, so no button leads to a
 * page that cannot show the group or only sends them back. The one rule for
 * the shell's banners and every paused module offer, so two "Manage billing"
 * buttons on a screen never part ways.
 */
export function useGroupBillingTarget(
  entityGroupId: string | null | undefined
): (task?: BillingAttentionTask | null) => string {
  const managed = useManagedGroupBilling(entityGroupId);
  return (task = null) => (managed ? manageSubscriptionPath(managed.key, task) : BUSINESS_BILLING_PATH);
}

/** Whether the billing alert this viewer was sent says the subscription is paused. */
export const isPausedAlert = (alert: BusinessBillingAlert | null | undefined): boolean =>
  alert?.status === 'paused';

/**
 * The selected business's paused state, from the payload it already carries.
 * Not paused with no business selected (preview mode), or when nothing told
 * this viewer so.
 */
export function usePausedModuleOffer(): PausedModuleOffer {
  const current = useBusinessOptional()?.currentBusiness;
  const billingTarget = useGroupBillingTarget(current?.entity_group_id);
  return pausedModuleOffer(current?.role === 'owner', isPausedAlert(current?.billing_alert), billingTarget('resume'));
}

/**
 * A module lock as it reads while paused: its reason, then the pause, and the
 * owner's Manage billing in place of the purchase button (nothing for anyone
 * else). Unchanged while not paused.
 *
 * `saidAbove`: the page already says the pause above this lock, with the
 * owner's Manage billing (the CRM banner, drawn while CRM itself is not held,
 * above every CRM Pro lock). The lock then keeps just its reason and no
 * button, so the line and the button are said once on the page rather than
 * once more per lock.
 */
export function pausedModuleLock(lock: ModuleLock, offer: PausedModuleOffer, saidAbove = false): ModuleLock {
  if (!offer.paused) return lock;
  if (saidAbove) return { message: lock.message };
  return {
    message: `${lock.message} ${offer.message}`,
    ...(offer.canManageBilling ? { ctaLabel: MANAGE_BILLING_LABEL, ctaPath: offer.billingPath } : {}),
  };
}

/**
 * The row a module banner draws in place of its purchase button while paused:
 * Manage billing for the owner, and the line saying why nothing is offered.
 */
export const PausedModulesNote: React.FC<{ offer: PausedModuleOffer }> = ({ offer }) => {
  const navigate = useNavigate();

  return (
    <XStack gap={8} alignItems="center" flexWrap="wrap">
      {offer.canManageBilling && (
        <GBButton size="sm" variant="primary" onPress={() => navigate(offer.billingPath)}>
          {MANAGE_BILLING_LABEL}
        </GBButton>
      )}
      <GBText variant="bodySmall" color="secondary">
        {offer.message}
      </GBText>
    </XStack>
  );
};
